import { Style } from '@capacitor/status-bar';
import { LocalNotifications } from '@capacitor/local-notifications';
import { native } from './index';
import { markFired } from '@shared/lib/native/localReminders';

type Router = {
  history: { back: () => void };
  navigate: (to: string) => void;
};

type QueryClient = {
  invalidateQueries: (opts: { stale: boolean }) => void;
};

/**
 * Mount native lifecycle listeners once on app start.
 *
 * Must be called inside `if (native.isNative)` — the listeners are no-ops
 * in the browser but registering them on web adds unnecessary overhead.
 *
 * @param router  TanStack Router instance (for deep links + back button)
 * @param queryClient  TanStack Query client (for cache invalidation on resume)
 */
export function mountNativeLifecycle(router: Router, queryClient: QueryClient): () => void {
  const cleanupFns: Array<() => void> = [];

  // ── StatusBar: sync with OS theme ───────────────────────────────────
  function syncStatusBarStyle() {
    const isDark = document.documentElement.classList.contains('dark');
    native.statusBar
      .setStyle({ style: isDark ? Style.Dark : Style.Light })
      .catch(() => {/* non-critical */});
  }
  syncStatusBarStyle();

  // Re-sync when the ThemeProvider toggles the .dark class.
  const themeObserver = new MutationObserver(syncStatusBarStyle);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  cleanupFns.push(() => themeObserver.disconnect());

  // ── Deep links ───────────────────────────────────────────────────────
  // App plugin delivers appUrlOpen when the OS opens the app via a universal
  // link (iOS) or app link (Android). We strip the origin and navigate to the
  // path so TanStack Router handles the route guard and redirect logic.
  native.app
    .addListener('appUrlOpen', ({ url }) => {
      try {
        const { pathname, search } = new URL(url);
        router.navigate(`${pathname}${search}`);
      } catch {
        // malformed URL — ignore
      }
    })
    .then((handle) => cleanupFns.push(() => { void handle.remove(); }))
    .catch(() => {/* non-critical */});

  // ── Android back button ──────────────────────────────────────────────
  // Capacitor fires this before the system default (which would minimise the
  // app). We handle navigation ourselves so the system default is suppressed.
  native.app
    .addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        router.history.back();
      } else {
        // Dispatch a DOM event so the active route can show a confirm-exit
        // dialog (see prompt 02 for the dialog implementation).
        window.dispatchEvent(new CustomEvent('mobileExitConfirm'));
      }
    })
    .then((handle) => cleanupFns.push(() => { void handle.remove(); }))
    .catch(() => {/* non-critical */});

  // ── Foreground resume: invalidate short-TTL queries ──────────────────
  // When the app returns to the foreground after >30 s in the background,
  // treat all queries as stale so the user sees fresh data immediately.
  let backgroundedAt: number | null = null;
  const RESUME_STALE_THRESHOLD_MS = 30_000;

  native.app
    .addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        backgroundedAt = Date.now();
        return;
      }
      const elapsed = backgroundedAt != null ? Date.now() - backgroundedAt : 0;
      if (elapsed > RESUME_STALE_THRESHOLD_MS) {
        queryClient.invalidateQueries({ stale: true });
      }
      backgroundedAt = null;
    })
    .then((handle) => cleanupFns.push(() => { void handle.remove(); }))
    .catch(() => {/* non-critical */});

  // ── Local notification taps ──────────────────────────────────────────
  // Fired when the user taps a local (offline) reminder notification.
  // Marks the reminder as fired and routes to its deep link.
  LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
    const extra = action.notification?.extra ?? {};
    const reminderId = typeof extra.reminder_id === 'string' ? extra.reminder_id : null;
    const deepLink = typeof extra.deep_link === 'string' ? extra.deep_link : null;
    if (reminderId) void markFired(reminderId);
    if (deepLink) router.navigate(deepLink);
  })
    .then((handle) => cleanupFns.push(() => { void handle.remove(); }))
    .catch(() => {/* non-critical */});

  // Return a teardown function for StrictMode double-invoke safety.
  return () => cleanupFns.forEach((fn) => fn());
}
